/**
 * Data service — all API calls to the Node.js backend.
 * Replaces window.DataService (PHP api.php calls).
 */
import { apiGet, apiPost, apiPut, apiDelete, apiPostForm } from './api';
import type { Product, FileItem } from '../stores/appStore';
import type { Transform } from '../stores/types';
import { PRODUCTS_DATA } from '../constants';

// ── Files ──────────────────────────────────────────────────────────────

export async function loadFiles(): Promise<FileItem[]> {
  try {
    const data = await apiGet<{ success: boolean; files: FileItem[] }>(
      '/api/files?exclude_cloud=1&t=' + Date.now(),
    );
    return data.files || [];
  } catch (e) {
    console.error('Ошибка загрузки файлов:', e);
    return [];
  }
}

// ── Config ─────────────────────────────────────────────────────────────

export async function loadConfig(): Promise<Product[]> {
  try {
    const data = await apiGet<{ success: boolean; config: Product[] }>(
      '/api/config?t=' + Date.now(),
    );
    return data.config || [];
  } catch (e) {
    console.error('Ошибка загрузки конфига:', e);
    return [];
  }
}

export async function saveConfig(products: Product[]): Promise<void> {
  try {
    await apiPut('/api/config', { products });
  } catch (e) {
    console.error('Ошибка сохранения конфига:', e);
  }
}

// ── Prints Config ──────────────────────────────────────────────────────

export interface PrintConfig {
  transforms: Record<string, Transform>;
  productTransforms: Record<string, Transform>;
  lastModified: number;
}

export async function loadPrintsConfig(
  printName?: string | null,
): Promise<PrintConfig | Record<string, PrintConfig> | null> {
  try {
    let url = '/api/config/prints?t=' + Date.now();
    if (printName) url += '&print_name=' + encodeURIComponent(printName);
    const data = await apiGet<{ success: boolean; config: unknown }>(url);
    return (data.config as PrintConfig | Record<string, PrintConfig>) ?? null;
  } catch (e) {
    console.error('Ошибка загрузки конфига принтов:', e);
    return null;
  }
}

export async function savePrintConfig(printName: string, printData: PrintConfig): Promise<boolean> {
  try {
    const res = await apiPut('/api/config/prints', {
      print_name: printName,
      print_data: printData,
    });
    return !!(res as { success: boolean }).success;
  } catch (e) {
    console.error('Ошибка сохранения конфига принта:', e);
    return false;
  }
}

// ── SKU ────────────────────────────────────────────────────────────────

export async function generateSku(): Promise<string | null> {
  try {
    const data = await apiGet<{ success: boolean; sku: string }>('/api/config/sku?t=' + Date.now());
    return data.success ? data.sku : null;
  } catch (e) {
    console.error('Ошибка генерации артикула:', e);
    return null;
  }
}

// ── Lookup Article ─────────────────────────────────────────────────────

export async function findArticleBySource(
  sourceName: string,
): Promise<{ article: string; print_name?: string } | null> {
  try {
    const data = await apiPost<{
      success: boolean;
      found: boolean;
      article: string;
      print_name?: string;
    }>('/api/cloud/lookup-article', { source_name: sourceName });
    if (data.success && data.found) {
      return { article: data.article, print_name: data.print_name };
    }
    return null;
  } catch (e) {
    console.error('Ошибка поиска артикула:', e);
    return null;
  }
}

// ── Folders ────────────────────────────────────────────────────────────

export async function loadFolders(
  galleryType: string,
  title: string,
): Promise<Record<string, string[]>> {
  try {
    const data = await apiGet<{ success: boolean; folders: Record<string, string[]> }>(
      `/api/folders?gallery_type=${encodeURIComponent(galleryType)}&title=${encodeURIComponent(title)}&t=${Date.now()}`,
    );
    return data.success ? data.folders || {} : {};
  } catch (e) {
    console.error('Ошибка загрузки папок:', e);
    return {};
  }
}

export async function saveFolders(
  galleryType: string,
  title: string,
  folders: Record<string, string[]>,
): Promise<boolean> {
  try {
    const data = await apiPut<{ success: boolean }>('/api/folders', {
      gallery_type: galleryType,
      title,
      folders,
    });
    return data.success;
  } catch (e) {
    console.error('Ошибка сохранения папок:', e);
    return false;
  }
}

// ── Upload ─────────────────────────────────────────────────────────────

export async function uploadFiles(fileList: FileList | File[]): Promise<FileItem[] | null> {
  if (!fileList || (fileList as FileList).length === 0) return null;
  try {
    const formData = new FormData();
    formData.append('type', 'upload');
    const files = Array.from(fileList);
    for (const f of files) formData.append('files', f);
    const data = await apiPostForm<{ success: boolean; files: FileItem[] }>(
      '/api/files/upload',
      formData,
    );
    return data.success ? data.files : null;
  } catch (e) {
    console.error('Ошибка загрузки файлов:', e);
    return null;
  }
}

export async function uploadToCloud(
  blob: Blob,
  fileName: string,
  article: string,
  category: string,
  printName?: string,
  productName?: string,
  sourceName?: string,
): Promise<boolean> {
  try {
    const formData = new FormData();
    formData.append('type', 'cloud');
    formData.append('article', article);
    formData.append('category', category);
    if (printName) formData.append('print_name', printName);
    if (productName) formData.append('product_name', productName);
    if (sourceName) formData.append('source_name', sourceName);
    formData.append('files', new File([blob], fileName, { type: 'image/png' }));
    await apiPostForm('/api/files/upload', formData);
    return true;
  } catch (e) {
    console.error('Ошибка загрузки в облако:', e);
    return false;
  }
}

// ── Delete ─────────────────────────────────────────────────────────────

export async function deleteCloudFile(params: {
  filename: string;
  article: string;
  category: string;
}): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/files/delete', params);
    return data.success;
  } catch (e) {
    console.error('Ошибка удаления файла:', e);
    return false;
  }
}

export async function deleteCloudCategory(params: {
  article: string;
  category: string;
}): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/files/delete-category', params);
    return data.success;
  } catch (e) {
    console.error('Ошибка удаления категории:', e);
    return false;
  }
}

export async function deleteCloudArticle(params: { article: string }): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/files/delete-article', params);
    return data.success;
  } catch (e) {
    console.error('Ошибка удаления артикула:', e);
    return false;
  }
}

export async function deleteFile(params: {
  filename: string;
  isAsset?: boolean;
}): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/files/delete', params);
    return data.success;
  } catch (e) {
    console.error('Ошибка удаления:', e);
    return false;
  }
}

// ── Cloud Meta ─────────────────────────────────────────────────────────

export async function updateCloudArticleMeta(params: {
  article: string;
  printName: string;
}): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/cloud/update-meta', {
      article: params.article,
      print_name: params.printName,
    });
    return data.success;
  } catch (e) {
    console.error('Ошибка обновления метаданных:', e);
    return false;
  }
}

// ── Create Cloud Article ───────────────────────────────────────────────

export async function createCloudArticleFromSource(params: {
  sourceName: string;
  article?: string;
  printName?: string;
  sourceRemote?: boolean;
  sourceUrl?: string;
  sourceThumb?: string;
}): Promise<Record<string, unknown> | null> {
  try {
    const body: Record<string, unknown> = {
      source_name: params.sourceName,
    };
    if (params.article) body.article = params.article;
    if (params.printName) body.print_name = params.printName;
    if (params.sourceRemote) body.source_remote = params.sourceRemote;
    if (params.sourceUrl) body.source_url = params.sourceUrl;
    if (params.sourceThumb) body.source_thumb = params.sourceThumb;

    const data = await apiPost<{ success: boolean } & Record<string, unknown>>(
      '/api/cloud/create-article',
      body,
    );
    return data.success ? data : null;
  } catch (e) {
    console.error('Ошибка создания артикула:', e);
    return null;
  }
}

// ── Cloud Categories ───────────────────────────────────────────────────

export interface CloudCategory {
  id: string;
  name: string;
  keywords: string[];
  prefixes: string[];
  is_active: boolean;
  priority: number;
}

export async function loadCloudCategories(): Promise<{
  categories: CloudCategory[];
  stats: Record<string, unknown>;
}> {
  try {
    const data = await apiGet<{
      success: boolean;
      categories: CloudCategory[];
      stats: Record<string, unknown>;
    }>('/api/cloud/categories?t=' + Date.now());
    if (!data.success) return { categories: [], stats: {} };
    return {
      categories: Array.isArray(data.categories) ? data.categories : [],
      stats: data.stats || {},
    };
  } catch (e) {
    console.error('Ошибка загрузки категорий:', e);
    return { categories: [], stats: {} };
  }
}

export async function saveCloudCategories(
  categories: CloudCategory[],
): Promise<CloudCategory[] | null> {
  try {
    const data = await apiPost<{ success: boolean; categories: CloudCategory[] }>(
      '/api/cloud/categories',
      { categories },
    );
    return data.success ? data.categories || [] : null;
  } catch (e) {
    console.error('Ошибка сохранения категорий:', e);
    return null;
  }
}

export async function setCloudFileCategory(params: {
  article: string;
  category: string;
  filename: string;
  categoryId: string;
}): Promise<boolean> {
  try {
    const data = await apiPost<{ success: boolean }>('/api/cloud/set-file-category', {
      article: params.article,
      category: params.category,
      filename: params.filename,
      category_id: params.categoryId,
    });
    return !!data.success;
  } catch (e) {
    console.error('Ошибка назначения категории:', e);
    return false;
  }
}

// ── Mass Upload ────────────────────────────────────────────────────────

export async function massUploadToCloud(
  fileList: FileList | File[],
  category = 'products',
  options: {
    allowNewArticles?: boolean;
    onProgress?: (done: number, total: number, fileName?: string) => void;
  } = {},
): Promise<{
  success: boolean;
  uploaded: number;
  results: unknown[];
  errors: unknown[];
  total: number;
}> {
  const { allowNewArticles = false, onProgress } = options;
  const files = Array.from(fileList);
  const total = files.length;
  const BATCH_SIZE = 3;
  let allResults: unknown[] = [];
  let allErrors: unknown[] = [];
  let uploaded = 0;

  for (let start = 0; start < total; start += BATCH_SIZE) {
    const batchEnd = Math.min(start + BATCH_SIZE, total);
    try {
      const formData = new FormData();
      formData.append('category', category);
      if (allowNewArticles) formData.append('allow_new_articles', '1');
      for (let i = start; i < batchEnd; i++) {
        formData.append('files', files[i]);
      }
      const data = await apiPostForm<{
        success: boolean;
        uploaded?: number;
        results?: unknown[];
        errors?: unknown[];
      }>('/api/files/mass-upload', formData);
      if (data.success) {
        uploaded += data.uploaded || 0;
        allResults = allResults.concat(data.results || []);
        allErrors = allErrors.concat(data.errors || []);
      } else {
        for (let i = start; i < batchEnd; i++) {
          allErrors.push({ file: files[i].name, error: 'Ошибка загрузки' });
        }
      }
    } catch (e) {
      for (let i = start; i < batchEnd; i++) {
        allErrors.push({ file: files[i].name, error: (e as Error).message });
      }
    }
    if (onProgress)
      onProgress(Math.min(batchEnd, total), total, files[Math.min(batchEnd - 1, total - 1)]?.name);
  }

  return { success: true, uploaded, results: allResults, errors: allErrors, total };
}

// ── Cloud List ─────────────────────────────────────────────────────────

export interface CloudListResult {
  files: FileItem[];
  total: number;
  page: number;
  perPage: number;
  hasMore: boolean;
}

export async function loadCloudPage(page = 1, perPage = 50): Promise<CloudListResult> {
  try {
    const data = await apiGet<{
      files?: FileItem[];
      articles?: FileItem[];
      total: number;
      page: number;
      per_page: number;
      has_more: boolean;
    }>(`/api/cloud?page=${page}&per_page=${perPage}&t=${Date.now()}`);
    return {
      files: data.files || data.articles || [],
      total: data.total || 0,
      page: data.page || page,
      perPage: data.per_page || perPage,
      hasMore: !!data.has_more,
    };
  } catch (e) {
    console.error('Ошибка загрузки облачных файлов:', e);
    return { files: [], total: 0, page, perPage, hasMore: false };
  }
}

// ── Presets ─────────────────────────────────────────────────────────────

export async function loadPresets(): Promise<Record<string, Transform>> {
  try {
    const data = await apiGet<{ success: boolean; presets: Record<string, Transform> }>(
      '/api/presets?t=' + Date.now(),
    );
    return data.success ? data.presets || {} : {};
  } catch (e) {
    console.error('Ошибка загрузки пресетов:', e);
    return {};
  }
}

export async function savePreset(
  name: string,
  data: Transform | Record<string, Transform>,
): Promise<boolean> {
  try {
    const res = await apiPost<{ success: boolean }>('/api/presets', { name, data });
    return res.success;
  } catch (e) {
    console.error('Ошибка сохранения пресета:', e);
    return false;
  }
}

export async function deletePreset(name: string): Promise<boolean> {
  try {
    const res = await apiDelete<{ success: boolean }>('/api/presets', { name });
    return res.success;
  } catch (e) {
    console.error('Ошибка удаления пресета:', e);
    return false;
  }
}

// ── Link Remote ────────────────────────────────────────────────────────

export async function linkRemote(params: {
  url: string;
  name: string;
  thumb?: string;
  prompt?: string;
  model?: string;
}): Promise<FileItem | null> {
  try {
    const data = await apiPost<{ success: boolean; file: FileItem }>(
      '/api/files/link-remote',
      params,
    );
    return data.success ? data.file : null;
  } catch (e) {
    console.error('Ошибка линковки:', e);
    return null;
  }
}

// ── Merge Products ─────────────────────────────────────────────────────

export function mergeProducts(savedConfig: Product[]): Product[] {
  if (savedConfig && Array.isArray(savedConfig) && savedConfig.length > 0) {
    return savedConfig;
  }
  return PRODUCTS_DATA.map((d) => ({
    ...d,
    enabled: true,
    tab: 'mockups' as const,
  }));
}

// ── Initialize ─────────────────────────────────────────────────────────

export async function initialize(): Promise<{
  files: FileItem[];
  products: Product[];
  printsConfig: Record<string, unknown>;
}> {
  try {
    const [files, config, printsConfig] = await Promise.all([
      loadFiles(),
      loadConfig(),
      loadPrintsConfig(),
    ]);
    const products = mergeProducts(config);
    return { files, products, printsConfig: (printsConfig as Record<string, unknown>) || {} };
  } catch (e) {
    console.error('Ошибка инициализации:', e);
    return { files: [], products: [], printsConfig: {} };
  }
}

// ── Updates (обновления) ──────────────────────────────────────────────

export interface UpdateItem {
  id: string;
  title: string;
  body: string;
  version: string;
  created_at: number;
}

export async function loadUpdates(): Promise<UpdateItem[]> {
  try {
    const data = await apiGet<{ success: boolean; updates: UpdateItem[] }>(
      '/api/updates?t=' + Date.now(),
    );
    return data.success ? data.updates || [] : [];
  } catch (e) {
    console.error('Ошибка загрузки обновлений:', e);
    return [];
  }
}

export async function createUpdate(params: {
  title: string;
  body: string;
  version?: string;
}): Promise<UpdateItem | null> {
  try {
    const data = await apiPost<{ success: boolean; update: UpdateItem }>('/api/updates', params);
    return data.success ? data.update : null;
  } catch (e) {
    console.error('Ошибка создания обновления:', e);
    return null;
  }
}

export async function deleteUpdate(id: string): Promise<boolean> {
  try {
    const data = await apiDelete<{ success: boolean }>('/api/updates', { id });
    return data.success;
  } catch (e) {
    console.error('Ошибка удаления обновления:', e);
    return false;
  }
}
